import { ChartDataset, defaults } from "chart.js";
import { color, HSLColor } from "d3-color";

import { ANALYSIS_DATA_TYPE } from "@/constants";

import { colorizeDataset } from "./colorizeDataset";

const HIGHLIGHTED_POINT_RADIUS = 5;
const HIGHLIGHTED_POINT_HOVER_RADIUS = 8;

const INSIGHT_POINT_BRIGHTNESS_SHIFT = -2;

/**
 * Modify a given dataset so have the defaults spread into arrays for easy modifications on the point level
 */
const populateDatasetDefaults = (dataset: ChartDataset<"line", number[]>) => {
  dataset.pointRadius = Array(dataset.data.length).fill(
    defaults.datasets.line.pointRadius
  );
  dataset.pointHoverRadius = Array(dataset.data.length).fill(
    defaults.datasets.line.pointHoverRadius
  );
  dataset.pointHitRadius = Array(dataset.data.length).fill(
    defaults.datasets.line.pointHitRadius
  );
  dataset.pointBackgroundColor = Array(dataset.data.length).fill(
    dataset.pointBackgroundColor
  );
  dataset.pointHoverBackgroundColor = Array(dataset.data.length).fill(
    dataset.pointHoverBackgroundColor
  );
  dataset.pointBorderColor = Array(dataset.data.length).fill(
    dataset.pointBorderColor
  );
  dataset.pointHoverBorderColor = Array(dataset.data.length).fill(
    dataset.pointHoverBorderColor
  );
  return dataset;
};

type PrepareDatasetSettings = {
  area?: boolean;
};

// Modify a single dataset
const prepareDataset = (
  _dataset: ChartDataset<"line", number[]>,
  settings: PrepareDatasetSettings = {}
) => {
  // Create a copy of the dataset to make sure we don't mutate the original
  const dataset = Object.assign({}, _dataset);

  // Save the defaults for later
  const untouched = {
    pointRadius: dataset.pointRadius,
    pointHoverRadius: dataset.pointHoverRadius,
    pointHitRadius: dataset.pointHitRadius,
    pointBackgroundColor: dataset.pointBackgroundColor,
    pointHoverBackgroundColor: dataset.pointHoverBackgroundColor,
  };

  // Unfold the defaults into arrays
  populateDatasetDefaults(dataset);

  // Set the fill to origin if we want to show an area chart
  if (settings.area) {
    dataset.fill = "origin";
  }

  dataset.data.forEach((point: number, index: number) => {
    // This is not a beautiful if-statement, but I found no quick around it with Typescript at this time.
    if (
      !Array.isArray(dataset.pointRadius) ||
      !Array.isArray(dataset.pointHitRadius) ||
      !Array.isArray(dataset.pointHoverRadius) ||
      !Array.isArray(dataset.pointBackgroundColor) ||
      !Array.isArray(dataset.pointHoverBackgroundColor) ||
      !Array.isArray(dataset.pointBorderColor) ||
      !Array.isArray(dataset.pointHoverBorderColor)
    )
      return;
    if (point === 0) {
      // Make sure these are not clickable by setting the hover target to 0 radius)
      dataset.pointHitRadius[index] = 0;
      // Make sure points with 0 value are not visible
      dataset.pointRadius[index] = 0;
      dataset.pointHoverRadius[index] = 0;
      return;
    }

    if (dataset.insights?.[index] != null) {
      // Separate styles for points with insights
      // Make sure points that have a insight is bigger
      dataset.pointRadius[index] = HIGHLIGHTED_POINT_RADIUS;
      dataset.pointHoverRadius[index] = HIGHLIGHTED_POINT_HOVER_RADIUS;

      // Shift the color for the points
      if (untouched.pointBackgroundColor == null) return;
      const shifted = color(
        untouched.pointBackgroundColor as HSLColor
      ).brighter(INSIGHT_POINT_BRIGHTNESS_SHIFT);

      dataset.pointBackgroundColor[index] = shifted;
      dataset.pointHoverBackgroundColor[index] = shifted;
      dataset.pointHoverBorderColor[index] = shifted;
      dataset.pointBorderColor[index] = shifted;
    }
    // ... keep the regular size

    dataset.hidden = false;
  });
  return dataset;
};

// Modify the datasets key of an object
const prepareDatasets = <
  T extends { datasets: ChartDataset<"line", number[]>[] },
>(
  { datasets: _datasets, ...rest }: T,
  settings: PrepareDatasetSettings = {}
): T => {
  const datasets = [..._datasets]
    .map((d, i) => colorizeDataset(d, i, settings.area || false))
    .map((d) => prepareDataset(d, settings));

  // If there's an "all" set, make sure that one is the only one that's visible

  if (
    datasets.some((dataset) => dataset.data_type === ANALYSIS_DATA_TYPE.ALL)
  ) {
    datasets.forEach((dataset) => {
      if (dataset.data_type !== ANALYSIS_DATA_TYPE.ALL) {
        dataset.hidden = true;
      }
    });
  } else {
    datasets.forEach((dataset) => {
      dataset.hidden = false;
    });
  }

  return { ...rest, datasets } as T;
};

export default prepareDatasets;
